import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';

import Tooltip from '@atlaskit/tooltip';
import { IconButton } from '@atlaskit/button/new';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ClockIcon from '@atlaskit/icon/core/clock';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import FieldTextIcon from '@atlaskit/icon-lab/core/field-text';
import { Box } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePageContentId } from '@confluence/page-context';
import {
	Attribution,
	ErrorDisplay,
	isUnauthorizedError,
	withErrorBoundary,
} from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { CONTENT_HISTORY } from '@confluence/named-routes';
import { useIsProductAdmin } from '@confluence/current-user';

import { useChangeHubNameDialog } from '../change-hub-name-dialog/useChangeHubNameDialog';
import { useDeactivateHubDialog } from '../unpublish-hub-dialog/useDeactivateHubDialog';

import type {
	HubActionsDropdownQuery as HubActionsDropdownQueryType,
	HubActionsDropdownQueryVariables,
} from './__types__/HubActionsDropdownQuery';
import { HubActionsDropdownQuery } from './HubActionsDropdownQuery.graphql';

export const i18n = defineMessages({
	ellipsisMenuLabel: {
		id: 'company-hub.hub-settings.general.dashboard.ellipsis-menu.label',
		defaultMessage: 'More actions',
		description: 'Label of ellipsis menu to adjust Company hub settings',
	},
	ellipsisMenuItemHistory: {
		id: 'company-hub.hub-settings.general.dashboard.ellipsis-menu.history',
		defaultMessage: 'History',
		description:
			'Ellipsis menu item in hub dashboard that allows the user to go to the Company hub history page',
	},
	ellipsisMenuItemRename: {
		id: 'company-hub.hub-settings.general.dashboard.ellipsis-menu.rename',
		defaultMessage: 'Rename',
		description:
			'Ellipsis menu item in hub dashboard that allows the user to go rename their Company hub',
	},
	ellipsisMenuItemDeactivate: {
		id: 'company-hub.hub-settings.general.dashboard.ellipsis-menu.deactivate',
		defaultMessage: 'Deactivate',
		description:
			'Ellipsis menu item in hub dashboard that allows the user to go to deactivate their Company hub',
	},
});

type HubActionsDropdownProps = {
	spaceKey: string;
	isHubPublished?: boolean;
};

const HubActionsDropdownComponent = ({ spaceKey, isHubPublished }: HubActionsDropdownProps) => {
	const dropdownItems: React.ReactNode[] = [];

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAnalyticsEvent = useCallback(
		(action) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: `hubActionsDropdown${action}`,
					source: 'hubActionsDropdown',
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const [contentId] = usePageContentId();
	const { data, error } = useQuery<HubActionsDropdownQueryType, HubActionsDropdownQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		HubActionsDropdownQuery,
		{
			variables: {
				spaceKey,
			},
		},
	);

	// History
	if (contentId && spaceKey) {
		dropdownItems.push(
			<DropdownItem
				key="history"
				elemBefore={<ClockIcon label="" />}
				onClick={() => fireAnalyticsEvent('History')}
				href={CONTENT_HISTORY.toUrl({ contentId, spaceKey })}
			>
				<FormattedMessage {...i18n.ellipsisMenuItemHistory} />
			</DropdownItem>,
		);
	}

	// Rename
	const isSpaceAdmin = data?.space?.currentUser?.isAdmin;
	const changeHubNameDialog = useChangeHubNameDialog({ isFirstTimeDialog: false });
	const openChangeHubNameDialog = useCallback(() => {
		changeHubNameDialog();
		fireAnalyticsEvent('Rename');
	}, [changeHubNameDialog, fireAnalyticsEvent]);

	dropdownItems.push(
		<DropdownItem
			key="rename"
			elemBefore={<FieldTextIcon label="" />}
			onClick={openChangeHubNameDialog}
			isDisabled={!isSpaceAdmin}
		>
			<FormattedMessage {...i18n.ellipsisMenuItemRename} />
		</DropdownItem>,
	);

	// Deactivate
	const { isProductAdmin } = useIsProductAdmin();
	const deactivateHubDialog = useDeactivateHubDialog({ contentId });
	const openDeactivateHubDialog = useCallback(() => {
		deactivateHubDialog();
		fireAnalyticsEvent('Deactivate');
	}, [deactivateHubDialog, fireAnalyticsEvent]);

	if (isHubPublished) {
		dropdownItems.push(
			<DropdownItem
				key="deactivate"
				elemBefore={<EyeOpenStrikethroughIcon label="" />}
				onClick={openDeactivateHubDialog}
				isDisabled={!isProductAdmin}
			>
				<FormattedMessage {...i18n.ellipsisMenuItemDeactivate} />
			</DropdownItem>,
		);
	}

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
			return <Box testId="unauthorized-error-state" />;
		} else {
			return (
				<ErrorDisplay error={error}>
					<Box testId="generic-error-state" />
				</ErrorDisplay>
			);
		}
	}

	return (
		<DropdownMenu
			placement="bottom-end"
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<Tooltip hideTooltipOnClick content={<FormattedMessage {...i18n.ellipsisMenuLabel} />}>
					<IconButton
						{...props}
						label={<FormattedMessage {...i18n.ellipsisMenuLabel} />}
						ref={triggerRef}
						icon={ShowMoreHorizontalIcon}
					/>
				</Tooltip>
			)}
		>
			<DropdownItemGroup>{dropdownItems}</DropdownItemGroup>
		</DropdownMenu>
	);
};

export const HubActionsDropdown = withErrorBoundary({
	attribution: Attribution.COMPANY_HUB,
})(HubActionsDropdownComponent);
